import React, { useRef, useState, useCallback } from "react";
import { useDrag, useDrop } from 'react-dnd';
import { PencilIcon, TrashIcon, DocumentDuplicateIcon } from "@heroicons/react/solid";
import { LuGripVertical } from "react-icons/lu";
import { classNames } from "./../../../../helpers/classNames";
import ItemMenu from "./../../../menus/ItemMenu";

const View = ({
  view,
  thisViewIndex,
  setSelectedViewId,
  selectedViewId,
  section,
  isColaborativeViews,
  myViewsLength,
  allViewsLength,
  onUpdateView,
  onDeleteView,
  onMoveView,
  onCopyView,
}) => {
  const ref = useRef(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingName, setEditingName] = useState(view.name);

  // Update local name when view name changes
  React.useEffect(() => {
    setEditingName(view.name);
  }, [view.name]);

  // Drag and drop setup
  const [{ handlerId }, drop] = useDrop({
    accept: 'view',
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item, monitor) {
      if (!ref.current) {
        return;
      }

      const dragIndex = item.index;
      const hoverIndex = thisViewIndex;
      const dragSection = item.section;

      // Don't replace items with themselves or cross sections
      if (dragIndex === hoverIndex && dragSection === section) {
        return;
      }

      // Only allow reordering within the same section
      if (dragSection !== section) {
        return;
      }

      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Perform the move
      onMoveView(dragIndex, hoverIndex, section);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: 'view',
    item: () => {
      return {
        id: view.smart_table_view_id,
        index: thisViewIndex,
        section
      };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const opacity = isDragging ? 0.4 : 1;
  drag(drop(ref));

  const calculateIsSelectedView = () => {
    if (view.smart_table_view_id === selectedViewId) return true;
    if (isColaborativeViews && myViewsLength > 0) return false;
    if (selectedViewId === 'default' && thisViewIndex === 0) return true;
    return false;
  };

  const isSelectedView = calculateIsSelectedView();

  const handleSetSelectedView = useCallback(() => {
    setSelectedViewId(view.smart_table_view_id);
  }, [setSelectedViewId, view.smart_table_view_id]);

  const handleDeleteView = useCallback(() => {
    if (allViewsLength === 1) return;
    onDeleteView(view.smart_table_view_id);
  }, [onDeleteView, view.smart_table_view_id, allViewsLength]);

  const handleCopyView = useCallback(() => {
    onCopyView(view);
  }, [onCopyView, view]);

  const handleStartEdit = useCallback(() => {
    setIsEditing(true);
    setEditingName(view.name);
  }, [view.name]);

  const handleSaveEdit = useCallback(() => {
    if (editingName.trim() === '' || editingName === view.name) {
      setEditingName(view.name); // Reset to original name if empty or unchanged
      setIsEditing(false);
      return;
    }

    onUpdateView(view.smart_table_view_id, {
      column: "name",
      value: editingName.trim()
    });
    setIsEditing(false);
  }, [editingName, view.name, view.smart_table_view_id, onUpdateView]);

  const handleCancelEdit = useCallback(() => {
    setEditingName(view.name);
    setIsEditing(false);
  }, [view.name]);

  const menuOptions = [
    {
      title: "Rename",
      action: handleStartEdit,
      icon: PencilIcon,
    },
    {
      title: "Copy",
      action: handleCopyView,
      icon: DocumentDuplicateIcon,
    },
    {
      title: "Delete",
      action: handleDeleteView,
      icon: TrashIcon,
      disabled: allViewsLength <= 1,
    },
  ];

  return (
    <div
      ref={ref}
      style={{ opacity }}
      data-handler-id={handlerId}
      className={classNames(
        "rounded-sm z-0 w-full flex justify-between items-center transition-all duration-200 group px-2",
        isSelectedView && "bg-blue-100",
        !isSelectedView && "hover:bg-gray-100",
        isDragging && "cursor-grabbing shadow-lg transform scale-105"
      )}
    >
      {/* Drag Handle */}
      <div
        className={classNames(
          "px-1 py-2 cursor-grab hover:bg-gray-200 rounded-l-sm transition-colors duration-150 opacity-0 group-hover:opacity-100 flex-shrink-0",
          isDragging && "cursor-grabbing opacity-100"
        )}
      >
        <LuGripVertical className="w-3 h-3 text-gray-400" />
      </div>

      {/* Main Content */}
      <div
        onClick={!isEditing ? handleSetSelectedView : undefined}
        className={classNames(
          "flex-1 py-2 px-2 min-w-0",
          !isEditing && "cursor-pointer"
        )}
      >
        {isEditing ? (
          <input
            className="input-primary w-full text-sm"
            type="text"
            value={editingName}
            onChange={(e) => setEditingName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSaveEdit();
              } else if (e.key === 'Escape') {
                handleCancelEdit();
              }
            }}
            onBlur={handleSaveEdit}
            autoFocus
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <div className="flex items-center space-x-2 min-w-0">
            <p className={classNames(
              "text-sm font-semibold text-left truncate max-w-[170px]",
              isSelectedView ? "text-blue-800" : "text-gray-700",
              view.isOptimistic && "italic opacity-75"
            )}>
              {view.name}
            </p>
            {view.isOptimistic && (
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full flex-shrink-0">
                Creating...
              </span>
            )}
          </div>
        )}
      </div>

      {/* Actions Menu */}
      {!isEditing && (
        <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-150" onClick={(e) => e.stopPropagation()}>
          <ItemMenu options={menuOptions} />
        </div>
      )}
    </div>
  );
};

export default View;
